import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IconDeviceFloppy } from "@tabler/icons-react";
import { useFormik } from "formik";
import * as yup from "yup";
import "quill/dist/quill.snow.css";
import "@assets/css/tour/add.css";
import "@assets/css/mode_tool/panel.css";
import InlineError from "@components/forms/InlineError";
import AppModal from "@components/modals/AppModal";
import TourAddMultimedia from "@components/tour/addMultimedia";
import CountryCitySelector from "@components/locations/CountryCitySelector";
import {
  ensureTourLanguages,
  resetTourDraft,
  setTourIsActive,
  setTourInclusiveAccessType,
  setTourButtonTitleValue,
  setTourBadgeTitleValue,
  setTourDurationValue,
  setTourDescriptionValue,
  setTourCountryValue,
  setTourRegionValue,
  setTourCityValue,
  setTourLocation,
  setTourStatus,
  setTourNameValue,
  setTourPointCount,
  setTourStopCount,
} from "@store/slices/tourDraftSlice";
import { createTourRequest, updateTourRequest } from "@services/tours/toursApi";
import { TOUR_STATUS_OPTIONS } from "@utils/tours/tourStatus";
import {
  createQuillEditor,
  destroyQuillEditor,
  getQuillHtml,
} from "@utils/quill/editor";

// Converts language records into a normalized list for the UI.
function buildLanguageList(languages = []) {
  return languages
    .map((language) => ({
      id: language.lng_id || language.id,
      iso: (language.lng_iso || language.iso || "").toLowerCase(),
      name: language.lng_name || language.name || "",
    }))
    .filter((language) => language.iso);
}

function buildTourValidationSchema(activeIsos = []) {
  const requiredIsos = activeIsos.includes("en") ? ["en"] : [];
  const buildLocalizedShape = () =>
    activeIsos.reduce((acc, iso) => {
      acc[iso] =
        iso === "en"
          ? yup.string().trim().required("EN is required.")
          : yup.string().nullable().transform(() => "");
      return acc;
    }, {});

  return yup.object({
    countryId: yup.string().trim().required("Country is required."),
    cityId: yup.string().trim().required("City is required."),
    status: yup.number().required("Status is required."),
    isActive: yup.boolean().required("Active flag is required."),
    bannerId: yup.string().trim().required("Banner is required."),
    snapshotId: yup
      .string()
      .trim()
      .required("Tour snapshot image is required."),
    tourImages: yup.array().of(yup.string().trim().required()),
    tourImagesCount: yup
      .number()
      .min(1, "At least one tour image is required.")
      .required("At least one tour image is required."),
    promoVideoCount: yup
      .number()
      .min(
        requiredIsos.length,
        "English must include one promo video.",
      )
      .required("English must include one promo video."),
    promoAudioCount: yup
      .number()
      .min(
        requiredIsos.length,
        "English must include one promo audio.",
      )
      .required("English must include one promo audio."),
    promoAudioTitlesValid: yup
      .boolean()
      .oneOf([true], "English promo audio must include a title.")
      .required("English promo audio must include a title."),
    country: yup
      .object()
      .shape(buildLocalizedShape())
      .required("Country is required."),
    buttonTitle: yup.object().shape(
      activeIsos.reduce((acc, iso) => {
        acc[iso] = yup.string().nullable().transform(() => "");
        return acc;
      }, {}),
    ),
    badgeTitle: yup.object().shape(
      activeIsos.reduce((acc, iso) => {
        acc[iso] = yup.string().nullable().transform(() => "");
        return acc;
      }, {}),
    ),
    region: yup
      .object()
      .shape(buildLocalizedShape())
      .required("Region is required."),
    city: yup
      .object()
      .shape(buildLocalizedShape())
      .required("City is required."),
    name: yup
      .object()
      .shape(buildLocalizedShape())
      .required("Name is required."),
    description: yup
      .object()
      .shape(buildLocalizedShape())
      .required("Description is required."),
    duration: yup
      .object()
      .shape(buildLocalizedShape())
      .required("Duration is required."),
    pointCount: yup
      .number()
      .integer("Point count must be an integer.")
      .min(0, "Point count cannot be negative.")
      .required("Point count is required."),
    stopCount: yup
      .number()
      .integer("Stop count must be an integer.")
      .min(0, "Stop count cannot be negative.")
      .required("Stop count is required."),
  });
}

function toPreviewText(value = "", max = 20) {
  const plain = String(value || "")
    .replace(/<[^>]*>/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!plain) return "";
  return plain.length > max ? `${plain.slice(0, max)}...` : plain;
}

function buildCreateField(values = {}, activeIsos = []) {
  return activeIsos.reduce((acc, iso) => {
    acc[iso] = String(values?.[iso] ?? "");
    return acc;
  }, {});
}

function buildUpdateField(values = {}, ids = {}, activeIsos = []) {
  return activeIsos.reduce((acc, iso) => {
    const normalizedValue = String(values?.[iso] ?? "");
    acc[iso] = {
      id: normalizedValue ? ids?.[iso] || "" : ids?.[iso] || "",
      value: normalizedValue,
    };
    if (!normalizedValue) {
      acc[iso].id = "";
    }
    return acc;
  }, {});
}

function buildTourAudioPayload(multimedia = [], activeIsos = []) {
  const normalizedIsos = (Array.isArray(activeIsos) ? activeIsos : [])
    .map((iso) => String(iso || "").toLowerCase().trim())
    .filter(Boolean);
  const allowedIsos = new Set(normalizedIsos);
  const groupedAudios = new Map();
  (Array.isArray(multimedia) ? multimedia : [])
    .filter((item) => {
      const mediaType = String(item?.mediaType || "").toLowerCase();
      const mimeType = String(item?.mimeType || "").toLowerCase();
      const languageIso = String(item?.languageIso || "")
        .toLowerCase()
        .trim();
      return (
        (mediaType === "audio" || mimeType.startsWith("audio/")) &&
        item?.mediaRole === "promoAudio" &&
        languageIso &&
        allowedIsos.has(languageIso)
      );
    })
    .forEach((audio) => {
      const iso = String(audio?.languageIso || "").toLowerCase().trim();
      const groupId = String(
        audio?.audioGroupId || audio?.sourceId || audio?.id || "",
      ).trim();
      if (!groupId) return;
      const current = groupedAudios.get(groupId) || {
        id: groupId,
        multimediaId: "",
        multimediaIds: {},
        title: {},
        audioUrls: {},
      };
      current.multimediaId =
        current.multimediaId || audio?.sourceId || audio?.id || "";
      current.multimediaIds[iso] = audio?.sourceId || audio?.id || "";
      current.title[iso] = String(audio?.audioTitles?.[iso] || "");
      current.audioUrls[iso] = audio?.url || "";
      groupedAudios.set(groupId, current);
    });
  return Array.from(groupedAudios.values())
    .map((item) => {
      const multimediaIds = {};
      const title = {};
      const audioUrls = {};
      normalizedIsos.forEach((iso) => {
        multimediaIds[iso] = String(item?.multimediaIds?.[iso] || "");
        title[iso] = String(item?.title?.[iso] || "");
        audioUrls[iso] = String(item?.audioUrls?.[iso] || "");
      });
      return {
        ...item,
        multimediaIds,
        title,
        audioUrls,
      };
    })
    .filter((item) => Object.keys(item?.audioUrls || {}).length > 0);
}

function buildTourPromoVideoPayload(multimedia = [], activeIsos = []) {
  const payload = activeIsos.reduce((acc, iso) => {
    const promoVideo = (multimedia || []).find((item) => {
      const mediaType = String(item?.mediaType || "").toLowerCase();
      const mimeType = String(item?.mimeType || "").toLowerCase();
      return (
        (mediaType === "video" || mimeType.startsWith("video/")) &&
        item?.mediaRole === "promoVideo" &&
        item?.languageIso === iso
      );
    });
    if (!promoVideo?.id) return acc;
    acc[iso] = {
      multimediaId: promoVideo?.sourceId || promoVideo?.id || "",
      url: promoVideo?.url || "",
    };
    return acc;
  }, {});
  return Object.keys(payload).length ? payload : null;
}

// Main create/update tour form rendered inside map tools panel.
function TourAddForm({
  languages,
  activeLanguageIso,
  onLocationPreview,
  onClearLocationPreview,
  onDone,
}) {
  const dispatch = useDispatch();
  const {
    countryId,
    tourId,
    selectedLanguages,
    cityId,
    countryName,
    cityName,
    buttonTitle: buttonTitleByLang,
    buttonTitleTranslationIds,
    badgeTitle: badgeTitleByLang,
    badgeTitleTranslationIds,
    name: nameByLang,
    nameTranslationIds,
    description: descriptionByLang,
    descriptionTranslationIds,
    duration: durationByLang,
    durationTranslationIds,
    country: countryByLang,
    countryTranslationIds,
    region: regionByLang,
    regionTranslationIds,
    city: cityByLang,
    cityTranslationIds,
    pointCount,
    stopCount,
    status,
    isActive,
    inclusiveAccessType,
    multimedia,
    banner,
    snapshot,
  } = useSelector((state) => state.tourDraft);
  const languageList = useMemo(() => buildLanguageList(languages), [languages]);
  const [editor, setEditor] = useState({
    isOpen: false,
    field: "description",
    languageIso: "",
    value: "",
  });
  const [quillHost, setQuillHost] = useState(null);
  const quillInstanceRef = useRef(null);
  const quillHandlerRef = useRef(null);
  const [descriptionHtml, setDescriptionHtml] = useState("");
  const [isDescriptionEditorReady, setIsDescriptionEditorReady] =
    useState(false);
  const [savingTour, setSavingTour] = useState(false);
  const activeIsos = useMemo(() => {
    const normalizedSelected = Array.from(
      new Set(
        (Array.isArray(selectedLanguages) ? selectedLanguages : [])
          .map((item) => String(item?.iso || "").toLowerCase().trim())
          .filter(Boolean),
      ),
    );
    const baseList = normalizedSelected.length
      ? languageList.filter((language) => normalizedSelected.includes(language.iso))
      : languageList;
    return baseList.map((language) => language.iso).filter(Boolean);
  }, [languageList, selectedLanguages]);
  const requiredIsos = useMemo(() => {
    return activeIsos.includes("en") ? ["en"] : [];
  }, [activeIsos]);
  const currentLanguageIso = useMemo(() => {
    if (activeIsos.includes(activeLanguageIso)) return activeLanguageIso;
    if (activeIsos.includes("en")) return "en";
    return activeIsos[0] || "";
  }, [activeIsos, activeLanguageIso]);
  const currentLanguageLabel = useMemo(() => {
    const currentLanguage = languageList.find(
      (language) => language.iso === currentLanguageIso,
    );
    return currentLanguage?.name || currentLanguageIso.toUpperCase();
  }, [currentLanguageIso, languageList]);
  const tourImagesCount = useMemo(
    () =>
      (multimedia || []).filter((item) => {
        const mediaType = String(item?.mediaType || "").toLowerCase();
        const mimeType = String(item?.mimeType || "").toLowerCase();
        return mediaType === "image" || mimeType.startsWith("image/");
      }).length,
    [multimedia],
  );
  const promoVideoCount = useMemo(() => {
    const videoIsos = new Set(
      (multimedia || [])
        .filter((item) => {
          const mediaType = String(item?.mediaType || "").toLowerCase();
          const mimeType = String(item?.mimeType || "").toLowerCase();
          return (
            (mediaType === "video" || mimeType.startsWith("video/")) &&
            item?.mediaRole === "promoVideo" &&
            item?.languageIso
          );
        })
        .map((item) => item.languageIso),
    );
    return videoIsos.size;
  }, [multimedia]);
  const promoAudioCount = useMemo(() => {
    const audioIsos = new Set(
      (multimedia || [])
        .filter((item) => {
          const mediaType = String(item?.mediaType || "").toLowerCase();
          const mimeType = String(item?.mimeType || "").toLowerCase();
          return (
            (mediaType === "audio" || mimeType.startsWith("audio/")) &&
            item?.mediaRole === "promoAudio" &&
            item?.languageIso
          );
        })
        .map((item) => item.languageIso),
    );
    return audioIsos.size;
  }, [multimedia]);
  const promoAudioTitlesValid = useMemo(() => {
    const promoAudios = (multimedia || []).filter((item) => {
      const mediaType = String(item?.mediaType || "").toLowerCase();
      const mimeType = String(item?.mimeType || "").toLowerCase();
      return (
        (mediaType === "audio" || mimeType.startsWith("audio/")) &&
        item?.mediaRole === "promoAudio"
      );
    });
    if (!requiredIsos.length) return false;
    return requiredIsos.every((iso) => {
      const items = promoAudios.filter((audio) => audio?.languageIso === iso);
      if (!items.length) return false;
      return items.every(
        (item) => String(item?.audioTitles?.[iso] || "").trim().length > 0,
      );
    });
  }, [multimedia, requiredIsos]);

  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues: {
      countryId,
      cityId,
      status,
      isActive,
      inclusiveAccessType,
      bannerId: banner?.id || "",
      snapshotId: snapshot?.id || "",
      tourImages: (multimedia || [])
        .filter((item) => {
          const mediaType = String(item?.mediaType || "").toLowerCase();
          const mimeType = String(item?.mimeType || "").toLowerCase();
          return mediaType === "image" || mimeType.startsWith("image/");
        })
        .map((item) => item.id)
        .filter((id) => Boolean(id) && id !== banner?.id),
      tourImagesCount,
      promoVideoCount,
      promoAudioCount,
      promoAudioTitlesValid,
      buttonTitle: buildCreateField(buttonTitleByLang, activeIsos),
      badgeTitle: buildCreateField(badgeTitleByLang, activeIsos),
      name: buildCreateField(nameByLang, activeIsos),
      description: buildCreateField(descriptionByLang, activeIsos),
      duration: buildCreateField(durationByLang, activeIsos),
      country: buildCreateField(countryByLang, activeIsos),
      region: buildCreateField(regionByLang, activeIsos),
      city: buildCreateField(cityByLang, activeIsos),
      pointCount,
      stopCount,
    },
    validationSchema: buildTourValidationSchema(activeIsos),
    onSubmit: () => {},
  });

  function touchAllRequiredFields() {
    const touchedByIso = activeIsos.reduce(
      (acc, iso) => {
        if (iso !== requiredIsos[0]) return acc;
        acc.name[iso] = true;
        acc.buttonTitle[iso] = true;
        acc.badgeTitle[iso] = true;
        acc.description[iso] = true;
        acc.duration[iso] = true;
        acc.country[iso] = true;
        acc.region[iso] = true;
        acc.city[iso] = true;
        return acc;
      },
      {
        name: {},
        buttonTitle: {},
        badgeTitle: {},
        description: {},
        duration: {},
        country: {},
        region: {},
        city: {},
      },
    );

    return formik.setTouched({
      countryId: true,
      cityId: true,
      status: true,
      isActive: true,
      bannerId: true,
      snapshotId: true,
      tourImagesCount: true,
      promoVideoCount: true,
      promoAudioCount: true,
      promoAudioTitlesValid: true,
      buttonTitle: touchedByIso.buttonTitle,
      badgeTitle: touchedByIso.badgeTitle,
      pointCount: true,
      stopCount: true,
      name: touchedByIso.name,
      description: touchedByIso.description,
      duration: touchedByIso.duration,
      country: touchedByIso.country,
      region: touchedByIso.region,
      city: touchedByIso.city,
    }, true);
  }

  useEffect(() => {
    const isos = languageList.map((language) => language.iso);
    dispatch(ensureTourLanguages(isos));
  }, [dispatch, languageList]);

  function pickLocalizedFieldError(fieldName) {
    const requiredIso = requiredIsos[0];
    if (!requiredIso) return "";
    const touchedField = formik.touched?.[fieldName];
    const fieldErrors = formik.errors?.[fieldName];
    if (!touchedField || typeof touchedField !== "object") return "";
    return String(fieldErrors?.[requiredIso] || "");
  }

  // Opens per-language modal editor for description.
  function openEditor(field, languageIso) {
    const source = descriptionByLang;
    const value = source[languageIso] || "";
    setEditor({
      isOpen: true,
      field,
      languageIso,
      value,
    });
    if (field === "description") {
      setDescriptionHtml(value);
    }
  }

  // Closes modal editor and resets temporary editor state.
  function closeEditor() {
    setEditor({
      isOpen: false,
      field: "description",
      languageIso: "",
      value: "",
    });
    setIsDescriptionEditorReady(false);
  }

  // Persists current modal editor value into Redux draft.
  function saveEditor() {
    dispatch(
      setTourDescriptionValue({
        iso: editor.languageIso,
        value: descriptionHtml,
      }),
    );
    formik.setFieldValue(
      `description.${editor.languageIso}`,
      toPreviewText(descriptionHtml, 1000),
      true,
    );
    formik.setFieldTouched(`description.${editor.languageIso}`, true, true);
    closeEditor();
  }

  useEffect(() => {
    // Lazy-initializes rich text editor only for description modal.
    if (!editor.isOpen || editor.field !== "description") return;
    if (!quillHost || quillInstanceRef.current) return;

    const quill = createQuillEditor({
      container: quillHost,
      initialHtml: descriptionHtml,
    });
    if (!quill) return;

    quillHandlerRef.current = () => setDescriptionHtml(getQuillHtml(quill));
    quill.on("text-change", quillHandlerRef.current);
    quillInstanceRef.current = quill;
    setIsDescriptionEditorReady(true);
  }, [descriptionHtml, editor.field, editor.isOpen, quillHost]);

  useEffect(() => {
    // Destroys rich text editor when modal closes to avoid memory leaks.
    if (!editor.isOpen && quillInstanceRef.current) {
      destroyQuillEditor({
        container: quillHost,
        quill: quillInstanceRef.current,
        textChangeHandler: quillHandlerRef.current,
      });
      quillInstanceRef.current = null;
      quillHandlerRef.current = null;
      setIsDescriptionEditorReady(false);
      setQuillHost(null);
    }
  }, [editor.isOpen, quillHost]);

  useEffect(
    () => () => {
      // Safety cleanup if component unmounts while editor exists.
      if (!quillInstanceRef.current) return;
      destroyQuillEditor({
        container: quillHost,
        quill: quillInstanceRef.current,
        textChangeHandler: quillHandlerRef.current,
      });
      quillInstanceRef.current = null;
      quillHandlerRef.current = null;
    },
    [quillHost],
  );

  // Builds final payload and sends create/update request.
  async function handleSubmitTour() {
    const basePayload = {
      countryId,
      countryName,
      cityId,
      cityName,
      buttonTitle: tourId
        ? buildUpdateField(
            buttonTitleByLang,
            buttonTitleTranslationIds,
            activeIsos,
          )
        : buildCreateField(buttonTitleByLang, activeIsos),
      badgeTitle: tourId
        ? buildUpdateField(
            badgeTitleByLang,
            badgeTitleTranslationIds,
            activeIsos,
          )
        : buildCreateField(badgeTitleByLang, activeIsos),
      point_count: Number(pointCount) || 0,
      stop_count: Number(stopCount) || 0,
      status,
      isActive,
      InclussiveAccessType: inclusiveAccessType,
      tourImages: (multimedia || [])
        .filter((item) => {
          const mediaType = String(item?.mediaType || "").toLowerCase();
          const mimeType = String(item?.mimeType || "").toLowerCase();
          return mediaType === "image" || mimeType.startsWith("image/");
        })
        .map((item) => item.id)
        .filter((id) => Boolean(id) && id !== banner?.id),
      banner: banner?.id,
      snapshot: snapshot?.id,
      tourPromoVideo: buildTourPromoVideoPayload(multimedia, activeIsos),
      tourAudio: buildTourAudioPayload(multimedia, activeIsos),
    };

    const payload = tourId
      ? {
          ...basePayload,
          name: buildUpdateField(nameByLang, nameTranslationIds, activeIsos),
          description: buildUpdateField(
            descriptionByLang,
            descriptionTranslationIds,
            activeIsos,
          ),
          duration: buildUpdateField(
            durationByLang,
            durationTranslationIds,
            activeIsos,
          ),
          country: buildUpdateField(
            countryByLang,
            countryTranslationIds,
            activeIsos,
          ),
          region: buildUpdateField(
            regionByLang,
            regionTranslationIds,
            activeIsos,
          ),
          city: buildUpdateField(cityByLang, cityTranslationIds, activeIsos),
        }
      : {
          ...basePayload,
          name: buildCreateField(nameByLang, activeIsos),
          description: buildCreateField(descriptionByLang, activeIsos),
          duration: buildCreateField(durationByLang, activeIsos),
          country: buildCreateField(countryByLang, activeIsos),
          region: buildCreateField(regionByLang, activeIsos),
          city: buildCreateField(cityByLang, activeIsos),
        };

    try {
      const errors = await formik.validateForm();
      if (Object.keys(errors || {}).length) {
        console.log("createTour validation errors:", errors);
        await touchAllRequiredFields();
        return;
      }
    } catch {
      return;
    }

    // Required log to validate the payload sent to the backend.
    console.log("createTour payload:", payload);

    setSavingTour(true);
    try {
      if (tourId) {
        await updateTourRequest(tourId, payload);
      } else {
        await createTourRequest(payload);
      }
      dispatch(resetTourDraft());
      if (typeof onClearLocationPreview === "function")
        onClearLocationPreview();
      if (typeof onDone === "function") onDone();
    } catch {
    } finally {
      setSavingTour(false);
    }
  }

  return (
    <>
      <div className="tour-add-shell">
        <div className="tour-add-form map-tools-form-placeholder">
        {/* Name block: localized values edited per selected language. */}
        <div className="tour-add-form-group">
          <p className="tour-add-form-label">
            name
            {pickLocalizedFieldError("name") ? (
              <InlineError>{pickLocalizedFieldError("name")}</InlineError>
            ) : null}
          </p>
          <label className="field-label app-form-field">
            <input
              className="field-input"
              value={nameByLang[currentLanguageIso] || ""}
              placeholder={`Write name (${currentLanguageIso})`}
              onChange={(event) => {
                dispatch(
                  setTourNameValue({
                    iso: currentLanguageIso,
                    value: event.target.value,
                  }),
                );
                formik.setFieldValue(
                  `name.${currentLanguageIso}`,
                  event.target.value,
                  true,
                );
              }}
              onBlur={() => {
                formik.setFieldTouched(
                  `name.${currentLanguageIso}`,
                  true,
                  true,
                );
              }}
            />
          </label>
        </div>

        {/* Description block: localized rich text edited per selected language. */}
        <div className="tour-add-form-group">
          <p className="tour-add-form-label">
            description
            {pickLocalizedFieldError("description") ? (
              <InlineError>{pickLocalizedFieldError("description")}</InlineError>
            ) : null}
          </p>
          <button
            type="button"
            className={`field-label app-form-field field-preview-row ${descriptionByLang[currentLanguageIso] ? "filled" : ""}`}
            onClick={() => openEditor("description", currentLanguageIso)}
          >
            {descriptionByLang[currentLanguageIso] ? (
              <span
                className="field-preview-value field-preview-value-html"
                dangerouslySetInnerHTML={{
                  __html: String(descriptionByLang[currentLanguageIso] || ""),
                }}
              />
            ) : (
              <span className="field-preview-value">Complete</span>
            )}
          </button>
        </div>

        {/* Media block: main image + tour images + promo media + snapshot. */}
        <TourAddMultimedia
          languages={languageList}
          activeLanguageIso={currentLanguageIso}
          bannerValidationError={
            formik.touched.bannerId ? formik.errors.bannerId : ""
          }
          snapshotValidationError={
            formik.touched.snapshotId ? formik.errors.snapshotId : ""
          }
          tourImagesValidationError={
            formik.touched.tourImagesCount ? formik.errors.tourImagesCount : ""
          }
          promoVideoValidationError={
            formik.touched.promoVideoCount ? formik.errors.promoVideoCount : ""
          }
          promoAudioValidationError={
            formik.touched.promoAudioCount ? formik.errors.promoAudioCount : ""
          }
          promoAudioTitlesValidationError={
            formik.touched.promoAudioTitlesValid
              ? formik.errors.promoAudioTitlesValid
              : ""
          }
        />

        {/* Location block: country + city selection synchronized with main map. */}
        <div className="tour-add-form-group">
          <p className="tour-add-form-label">
            location
            {formik.touched.countryId && formik.errors.countryId ? (
              <InlineError>{formik.errors.countryId}</InlineError>
            ) : null}
            {formik.touched.cityId && formik.errors.cityId ? (
              <InlineError>{formik.errors.cityId}</InlineError>
            ) : null}
          </p>
          <CountryCitySelector
            value={{ countryId, cityId }}
            countryError={
              formik.touched.countryId ? formik.errors.countryId : ""
            }
            cityError={formik.touched.cityId ? formik.errors.cityId : ""}
            onChange={(payload) => {
              dispatch(setTourLocation(payload));
              formik.setFieldValue("countryId", payload?.countryId || "", true);
              formik.setFieldValue("cityId", payload?.cityId || "", true);
              formik.setFieldTouched("countryId", true, true);
              formik.setFieldTouched("cityId", true, true);
            }}
            onLocationPreview={onLocationPreview}
            onClearLocationPreview={onClearLocationPreview}
          />
        </div>

        {/* Localized country: manual value per selected language. */}
        <div className="tour-add-form-group">
          <p className="tour-add-form-label">
            country
            {pickLocalizedFieldError("country") ? (
              <InlineError>{pickLocalizedFieldError("country")}</InlineError>
            ) : null}
          </p>
          <label className="field-label app-form-field">
            <input
              className="field-input"
              value={countryByLang[currentLanguageIso] || ""}
              placeholder={`Write country (${currentLanguageIso})`}
              onChange={(event) => {
                dispatch(
                  setTourCountryValue({
                    iso: currentLanguageIso,
                    value: event.target.value,
                  }),
                );
                formik.setFieldValue(
                  `country.${currentLanguageIso}`,
                  event.target.value,
                  true,
                );
              }}
              onBlur={() =>
                formik.setFieldTouched(
                  `country.${currentLanguageIso}`,
                  true,
                  true,
                )
              }
            />
          </label>
        </div>

        {/* Localized region: manual value per selected language. */}
        <div className="tour-add-form-group">
          <p className="tour-add-form-label">
            region
            {pickLocalizedFieldError("region") ? (
              <InlineError>{pickLocalizedFieldError("region")}</InlineError>
            ) : null}
          </p>
          <label className="field-label app-form-field">
            <input
              className="field-input"
              value={regionByLang[currentLanguageIso] || ""}
              placeholder={`Write region (${currentLanguageIso})`}
              onChange={(event) => {
                dispatch(
                  setTourRegionValue({
                    iso: currentLanguageIso,
                    value: event.target.value,
                  }),
                );
                formik.setFieldValue(
                  `region.${currentLanguageIso}`,
                  event.target.value,
                  true,
                );
              }}
              onBlur={() =>
                formik.setFieldTouched(
                  `region.${currentLanguageIso}`,
                  true,
                  true,
                )
              }
            />
          </label>
        </div>

        {/* Localized city: manual value per selected language. */}
        <div className="tour-add-form-group">
          <p className="tour-add-form-label">
            city
            {pickLocalizedFieldError("city") ? (
              <InlineError>{pickLocalizedFieldError("city")}</InlineError>
            ) : null}
          </p>
          <label className="field-label app-form-field">
            <input
              className="field-input"
              value={cityByLang[currentLanguageIso] || ""}
              placeholder={`Write city (${currentLanguageIso})`}
              onChange={(event) => {
                dispatch(
                  setTourCityValue({
                    iso: currentLanguageIso,
                    value: event.target.value,
                  }),
                );
                formik.setFieldValue(
                  `city.${currentLanguageIso}`,
                  event.target.value,
                  true,
                );
              }}
              onBlur={() =>
                formik.setFieldTouched(`city.${currentLanguageIso}`, true, true)
              }
            />
          </label>
        </div>

        {/* Lifecycle block: business status and active flag required by CMS rules. */}
        <div className="tour-add-form-group">
          <p className="tour-add-form-label">
            status and active
            {formik.touched.status && formik.errors.status ? (
              <InlineError>{formik.errors.status}</InlineError>
            ) : null}
            {formik.touched.isActive && formik.errors.isActive ? (
              <InlineError>{formik.errors.isActive}</InlineError>
            ) : null}
          </p>
          <div className="tour-add-form-grid">
            <label className="field-label app-form-field">
              <span className="label-inline">status</span>
              <select
                className="field-input select-control"
                value={status}
                onChange={(event) => {
                  dispatch(setTourStatus(Number(event.target.value)));
                  formik.setFieldValue(
                    "status",
                    Number(event.target.value),
                    true,
                  );
                  formik.setFieldTouched("status", true, true);
                }}
              >
                {TOUR_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.value} - {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field-label app-form-field">
              <span className="label-inline">isActive</span>
              <select
                className="field-input select-control"
                value={isActive ? "true" : "false"}
                onChange={(event) => {
                  dispatch(setTourIsActive(event.target.value === "true"));
                  formik.setFieldValue(
                    "isActive",
                    event.target.value === "true",
                    true,
                  );
                  formik.setFieldTouched("isActive", true, true);
                }}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </label>
          </div>
        </div>

        {/* Duration block: localized values edited per selected language. */}
        <div className="tour-add-form-group">
          <p className="tour-add-form-label">
            tour duration
            {pickLocalizedFieldError("duration") ? (
              <InlineError>{pickLocalizedFieldError("duration")}</InlineError>
            ) : null}
          </p>
          <label className="field-label app-form-field">
            <input
              className="field-input"
              value={durationByLang[currentLanguageIso] || ""}
              placeholder={`Write duration (${currentLanguageIso})`}
              onChange={(event) => {
                dispatch(
                  setTourDurationValue({
                    iso: currentLanguageIso,
                    value: event.target.value,
                  }),
                );
                formik.setFieldValue(
                  `duration.${currentLanguageIso}`,
                  event.target.value,
                  true,
                );
              }}
              onBlur={() => {
                formik.setFieldTouched(
                  `duration.${currentLanguageIso}`,
                  true,
                  true,
                );
              }}
            />
          </label>
        </div>

        <div className="tour-add-form-group">
          <p className="tour-add-form-label">
            points and stops
            {formik.touched.pointCount && formik.errors.pointCount ? (
              <InlineError>{formik.errors.pointCount}</InlineError>
            ) : null}
            {formik.touched.stopCount && formik.errors.stopCount ? (
              <InlineError>{formik.errors.stopCount}</InlineError>
            ) : null}
          </p>
          <div className="tour-add-form-grid">
            <label className="field-label app-form-field">
              <span className="label-inline">point count</span>
              <input
                type="number"
                min="0"
                step="1"
                className="field-input"
                value={pointCount}
                onChange={(event) => {
                  const nextValue = Math.max(
                    0,
                    Number.parseInt(event.target.value || "0", 10) || 0,
                  );
                  dispatch(setTourPointCount(nextValue));
                  formik.setFieldValue("pointCount", nextValue, true);
                }}
                onBlur={() => formik.setFieldTouched("pointCount", true, true)}
              />
            </label>
            <label className="field-label app-form-field">
              <span className="label-inline">stop count</span>
              <input
                type="number"
                min="0"
                step="1"
                className="field-input"
                value={stopCount}
                onChange={(event) => {
                  const nextValue = Math.max(
                    0,
                    Number.parseInt(event.target.value || "0", 10) || 0,
                  );
                  dispatch(setTourStopCount(nextValue));
                  formik.setFieldValue("stopCount", nextValue, true);
                }}
                onBlur={() => formik.setFieldTouched("stopCount", true, true)}
              />
            </label>
          </div>
        </div>

        <div className="tour-add-form-group tour-add-form-group-block">
          <p className="tour-add-form-label">labels and badges</p>
          <div className="tour-add-form-grid">
            <label className="field-label app-form-field">
              <span className="label-inline">
                button title
                {pickLocalizedFieldError("buttonTitle") ? (
                  <InlineError>{pickLocalizedFieldError("buttonTitle")}</InlineError>
                ) : null}
              </span>
              <input
                className="field-input"
                value={buttonTitleByLang[currentLanguageIso] || ""}
                placeholder={`Write button title (${currentLanguageIso})`}
                onChange={(event) => {
                  dispatch(
                    setTourButtonTitleValue({
                      iso: currentLanguageIso,
                      value: event.target.value,
                    }),
                  );
                  formik.setFieldValue(
                    `buttonTitle.${currentLanguageIso}`,
                    event.target.value,
                    true,
                  );
                }}
                onBlur={() => {
                  formik.setFieldTouched(
                    `buttonTitle.${currentLanguageIso}`,
                    true,
                    true,
                  );
                }}
              />
            </label>

            <label className="field-label app-form-field">
              <span className="label-inline">
                badge title
                {pickLocalizedFieldError("badgeTitle") ? (
                  <InlineError>{pickLocalizedFieldError("badgeTitle")}</InlineError>
                ) : null}
              </span>
              <input
                className="field-input"
                value={badgeTitleByLang[currentLanguageIso] || ""}
                placeholder={`Write badge title (${currentLanguageIso})`}
                onChange={(event) => {
                  dispatch(
                    setTourBadgeTitleValue({
                      iso: currentLanguageIso,
                      value: event.target.value,
                    }),
                  );
                  formik.setFieldValue(
                    `badgeTitle.${currentLanguageIso}`,
                    event.target.value,
                    true,
                  );
                }}
                onBlur={() => {
                  formik.setFieldTouched(
                    `badgeTitle.${currentLanguageIso}`,
                    true,
                    true,
                  );
                }}
              />
            </label>
          </div>
        </div>

        <div className="tour-add-form-group">
          <label className="field-label app-form-field">
            <span className="label-inline">InclussiveAccessType</span>
            <input
              type="checkbox"
              checked={Boolean(inclusiveAccessType)}
              onChange={(event) => {
                const nextValue = Boolean(event.target.checked);
                dispatch(setTourInclusiveAccessType(nextValue));
                formik.setFieldValue("inclusiveAccessType", nextValue, true);
              }}
            />
          </label>
        </div>

        <AppModal
          isOpen={editor.isOpen}
          onClose={closeEditor}
          title={`${editor.field} (${editor.languageIso})`}
          size="lg"
        >
          {/* Modal editor used for each translated field. */}
          <div className="tour-add-editor">
            {!isDescriptionEditorReady ? (
              <p className="tour-add-editor-loading">Loading editor...</p>
            ) : null}
            <div ref={setQuillHost} className="tour-add-quill" />
            <div className="tour-add-editor-actions">
              <button
                type="button"
                className="button-primary"
                onClick={saveEditor}
              >
                Save
              </button>
              <button
                type="button"
                className="button-ghost"
                onClick={closeEditor}
              >
                Cancel
              </button>
            </div>
          </div>
        </AppModal>
        </div>
      </div>
      <div className="mode-route-tool-steps tour-add-save-row">
        <button
          type="button"
          className={`app-icon-btn app-icon-btn--add mode-route-tool-route-btn ${savingTour ? "is-disabled" : ""} ${savingTour ? "is-loading" : ""}`}
          onClick={handleSubmitTour}
          disabled={savingTour}
          aria-disabled={savingTour}
          aria-label={tourId ? "Update tour" : "Save tour"}
          title={tourId ? "Update tour" : "Save tour"}
        >
          {savingTour ? (
            <span className="mode-route-tool-btn-spinner" />
          ) : (
            <>
              Save
              <IconDeviceFloppy size={16} />
            </>
          )}
        </button>
      </div>
    </>
  );
}

export default TourAddForm;
